import { useState, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { EASE } from '../data/site';
import { WipeLink } from '../components/Wipe';
import { MaskedLines, FadeUp, Eyebrow } from '../components/Reveal';
import Footer from '../components/Footer';

const PINK = '#F9526B';

const LAYERS = [
  {
    n: 'Layer 1', t: 'Capture', s: 'The Foundation',
    img: '/assets/book/img-p244-44595.jpg',
    points: ['A dedicated counter device, billing system, purchase register, credit tracker and operational console in one', 'Barcode billing with quick quantity input for open items; totals calculated automatically', 'Cash, digital or credit, credit links to the customer by phone or name', 'Purchase entries scan or create products instantly, generating barcodes on the fly', 'Wastage captured in three taps: select product, quantity, save', 'Manual billing still works, missing fields never block the process. Consistent capture over perfect data'],
  },
  {
    n: 'Layer 2', t: 'Understanding', s: 'From Data to Control',
    img: '/assets/book/img-p253-46473.jpg',
    points: ['A daily snapshot answers one question: what happened today? Sales, credit given, cash received', 'Weekly trends replace memory, what is moving, what is slowing, where losses happen', 'Patterns convert into actions: what to reorder, what to reduce, where money is stuck', 'A conversational AI layer, ask for pending credit, slow movers, or sales, and act through prompts', 'The shop moves from memory to visibility, guessing to understanding, reaction to awareness'],
  },
  {
    n: 'Layer 3', t: 'Expansion', s: 'Growth Enablement',
    img: '/assets/book/img-p256-46893.jpg',
    points: ['A digital shopfront generated automatically from existing product, price and stock data, no setup', 'Customers order via link or QR; packing lists print, staff prepare, delivery follows the existing process', 'Offline and online run through one system, no separate inventory, no duplication', 'Future capability: platform integrations, local fulfilment, expanded reach, optional, never forced', 'Growth without disrupting the base operation'],
  },
];

const Saarthi = () => {
  const [layer, setLayer] = useState(0);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);

  return (
    <div className="bg-paper" data-testid="saarthi-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[92vh] flex flex-col justify-end overflow-hidden" style={{ background: PINK }}>
        <div className="bandhani-dot absolute inset-0 text-paper/[0.07] pointer-events-none" />
        <div className="relative px-5 md:px-10 lg:px-24 pt-32 pb-10 md:pb-14">
          <FadeUp y={14}>
            <p className="text-paper/75 text-xs md:text-sm font-semibold uppercase tracking-[0.35em]">
              A Conceptual Case Study
            </p>
          </FadeUp>
          <MaskedLines
            onLoad
            delay={0.25}
            lines={['Retail', 'Saarthi.']}
            className="mt-4 text-paper text-[18vw] md:text-[11vw] font-light tracking-tight leading-[0.9]"
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="mt-6 text-paper/85 text-base md:text-lg max-w-xl leading-relaxed"
          >
            One imagination of how the 3-Layer Approach could take tangible form, a
            product service system for the kirana counter.
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-4 text-paper/60 text-[11px] md:text-xs uppercase tracking-[0.3em]"
          >
            A visualization of the framework, not the outcome itself
          </motion.p>
        </div>
        <div className="relative px-5 md:px-10 lg:px-24 pb-16">
          <motion.div
            initial={{ clipPath: 'inset(100% 0 0 0)' }}
            animate={{ clipPath: 'inset(0% 0 0 0)' }}
            transition={{ delay: 0.5, duration: 1.2, ease: EASE }}
            className="relative overflow-hidden"
          >
            <motion.img
              src="/assets/book/img-p247-45345.jpg"
              alt="Retail Saarthi concept device at a kirana counter"
              style={{ y: imgY, scale: 1.15 }}
              className="w-full h-[46vh] md:h-[62vh] object-cover"
            />
          </motion.div>
          <p className="mt-3 text-paper/60 text-[11px] uppercase tracking-[0.25em]">Concept visualisation, AI courtesy Vizcom</p>
        </div>
      </section>

      {/* WHY THIS EXISTS */}
      <section className="px-5 md:px-10 lg:px-24 py-24 md:py-36" data-testid="saarthi-why">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Eyebrow color={PINK}>Reading This Correctly</Eyebrow>
            <MaskedLines
              lines={['The framework is', 'the outcome.', 'This is one way', 'to see it.']}
              className="mt-10 text-4xl md:text-6xl font-light tracking-tight leading-[1.05]"
            />
          </div>
          <FadeUp delay={0.15} className="lg:col-span-5 flex flex-col justify-end">
            <p className="text-base md:text-lg text-ink/70 leading-relaxed">
              The project resolves into the 3-Layer Approach and the system around it. Retail
              Saarthi sits downstream of that resolution, a conceptual translation showing how
              the framework's layers could become a dedicated counter device and a companion
              application, without prescribing that this is the only form the framework can take.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* WHAT IT IS */}
      <section className="bg-brand-black text-paper px-5 md:px-10 lg:px-24 py-24 md:py-36" data-testid="saarthi-what">
        <Eyebrow color="#F9526B">The Concept</Eyebrow>
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <FadeUp delay={0.1} className="lg:col-span-7">
            <p className="text-lg md:text-2xl font-normal leading-relaxed text-paper/85">
              Two tightly connected components, a <span className="text-brand-pink">dedicated counter device</span> and
              a <span className="text-brand-pink"> supporting mobile application</span>. The device captures every
              transaction at the point of action, barcode billing, purchase entries, credit
              records, directly within daily operations. The application processes that
              continuous stream into clear views of cash flow, stock movement, sales patterns
              and outstanding credit.
            </p>
            <p className="mt-6 text-sm md:text-base text-paper/55 leading-relaxed max-w-2xl">
              By converting everyday actions into consistent, traceable data without adding
              operational effort, the system builds a stable operational backbone, enabling
              better decision making while preserving the existing rhythm of the counter.
            </p>
          </FadeUp>
          <FadeUp delay={0.2} className="lg:col-span-5">
            <img src="/assets/book/img-p240-43861.jpg" alt="Retail Saarthi device concepts" data-testid="saarthi-devices-img" className="w-full aspect-[16/10] object-cover border border-white/15" />
            <p className="mt-3 text-[11px] uppercase tracking-[0.25em] text-paper/40">Device concept family, AI courtesy Vizcom</p>
          </FadeUp>
        </div>
      </section>

      {/* 3 LAYERS */}
      <section className="bg-brand-black text-paper px-5 md:px-10 lg:px-24 pb-24 md:pb-36" data-testid="saarthi-layers">
        <Eyebrow color="#F9526B">Three Layers, One Flow</Eyebrow>
        <div className="mt-10 flex flex-wrap gap-3" role="tablist" data-testid="layer-tabs">
          {LAYERS.map((l, i) => (
            <button
              key={i}
              role="tab"
              aria-selected={layer === i}
              onClick={() => setLayer(i)}
              data-testid={`layer-tab-${i + 1}`}
              className={`px-6 md:px-8 py-3.5 text-xs md:text-sm font-medium uppercase tracking-[0.2em] border transition-colors duration-300 ${
                layer === i
                  ? 'bg-paper text-brand-black border-paper'
                  : 'border-paper/30 text-paper/70 hover:border-paper/70'
              }`}
            >
              {l.n}, {l.t}
            </button>
          ))}
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={layer}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start"
            data-testid="layer-panel"
          >
            <div className="lg:col-span-6">
              <p className="text-xs uppercase tracking-[0.3em] text-brand-pink">{LAYERS[layer].s}</p>
              <p className="mt-4 text-4xl md:text-6xl font-light tracking-tight">{LAYERS[layer].t}.</p>
              <ul className="mt-8 space-y-4">
                {LAYERS[layer].points.map((p, i) => (
                  <li key={i} className="flex gap-4 text-sm md:text-base text-paper/75 leading-relaxed">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-pink" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-6">
              <div className="overflow-hidden border border-white/15">
                <motion.img
                  key={LAYERS[layer].img}
                  src={LAYERS[layer].img}
                  alt={`${LAYERS[layer].t} layer`}
                  initial={{ scale: 1.08 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 1.1, ease: EASE }}
                  className="w-full aspect-[4/3] object-cover"
                />
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </section>

      {/* FINAL POSITION */}
      <section className="px-5 md:px-10 lg:px-24 py-24 md:py-36" data-testid="saarthi-position">
        <Eyebrow color={PINK}>Final Position</Eyebrow>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-2 max-w-5xl">
          {[
            ['It does not replace instinct.', 'It supports it.'],
            ['It does not force discipline.', 'It builds it.'],
            ['It does not demand change.', 'It enables it.'],
          ].map(([a, b], i) => (
            <FadeUp key={i} delay={i * 0.08}>
              <p className="border-t border-ink/15 py-7 text-2xl md:text-3xl font-light tracking-tight leading-tight">
                <span className="text-support line-through decoration-brand-pink/60 decoration-2">{a}</span><br />
                <span style={{ color: PINK }}>{b}</span>
              </p>
            </FadeUp>
          ))}
          <FadeUp delay={0.3}>
            <p className="border-t border-ink/15 py-7 text-2xl md:text-3xl font-light tracking-tight leading-tight">
              <span className="text-ink">Nothing changes</span><br />
              <span className="text-support">in how the shop runs.</span>
            </p>
          </FadeUp>
        </div>
        <FadeUp delay={0.2}>
          <p className="mt-12 text-base md:text-lg text-ink/70 leading-relaxed max-w-2xl">
            But everything that happens within it becomes visible, understandable, and usable.
          </p>
        </FadeUp>
      </section>

      {/* BACK TO RESOLUTION */}
      <WipeLink
        to="/chapter/resolution"
        data-testid="back-to-resolution"
        className="group relative block overflow-hidden bg-brand-navy px-5 md:px-10 lg:px-24 py-24 md:py-32"
      >
        <div className="bandhani-dot absolute inset-0 text-paper/[0.06] pointer-events-none" />
        <p className="relative text-paper/60 text-xs uppercase tracking-[0.35em]">Return to</p>
        <div className="relative mt-4 flex items-end justify-between gap-6">
          <span className="text-paper text-5xl md:text-8xl font-light tracking-tight leading-none transition-transform duration-500 group-hover:translate-x-4">
            05, Resolution
          </span>
          <span className="text-paper text-4xl md:text-6xl transition-transform duration-500 group-hover:translate-x-3 group-hover:-translate-y-1">→</span>
        </div>
      </WipeLink>
      <Footer />
    </div>
  );
};

export default Saarthi;
